<template>
  <div class="relative" ref="container">
    <button
        @click="toggle"
        class="relative p-2 text-text-secondary hover:text-text hover:bg-background-lighter rounded-full transition-colors"
        aria-label="Notifications"
        title="Notifications"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
      <span
          v-if="unreadCount > 0"
          class="absolute -top-1 -right-1 bg-red-500 text-white rounded-full text-xs min-w-[20px] h-5 px-1 flex items-center justify-center font-bold shadow-lg"
      >
        {{ unreadCount > 9 ? '9+' : unreadCount }}
      </span>
    </button>

    <Transition name="notif">
      <div
          v-if="open"
          class="absolute right-0 top-full mt-2 z-50 w-80 sm:w-96 bg-background border border-background-lighter rounded-2xl shadow-2xl max-h-[70vh] flex flex-col overflow-hidden animate-slideDown"
      >
          <div class="flex items-center justify-between px-5 py-4 border-b border-background-lighter">
            <h3 class="text-lg font-semibold text-text">Notifications</h3>
            <div class="flex items-center gap-2">
              <button
                  v-if="unreadCount > 0"
                  @click="markAllRead"
                  class="text-xs text-primary hover:text-primary-dark transition-colors font-medium"
              >
                Mark all as read
              </button>
              <button
                  @click="close"
                  class="p-1 text-text-secondary hover:text-text rounded-lg hover:bg-background-lighter transition-colors"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto">
            <!-- Prompt: permission default -->
            <div v-if="showNotifPrompt" class="mx-4 mt-3 mb-1 px-4 py-3 bg-primary/10 rounded-xl flex items-center gap-3">
              <span class="text-lg">🔔</span>
              <p class="text-xs text-text-secondary flex-1">Get notified even when you're away</p>
              <button
                  @click="enableDesktopNotifs"
                  class="text-xs font-semibold text-primary hover:text-primary-dark whitespace-nowrap transition-colors"
              >
                Enable
              </button>
            </div>
            <!-- Prompt: permission denied -->
            <div v-if="showDeniedPrompt" class="mx-4 mt-3 mb-1 px-4 py-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-xl">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p class="text-xs text-text-secondary flex-1">Notifications blocked</p>
                <button
                    @click="enableDesktopNotifs"
                    class="text-xs font-medium text-primary hover:text-primary-dark whitespace-nowrap transition-colors"
                >
                  Try again
                </button>
                <button
                    @click="showDeniedHelp = !showDeniedHelp"
                    class="text-xs text-red-600 dark:text-red-400 underline hover:no-underline whitespace-nowrap"
                >
                  {{ showDeniedHelp ? 'Hide' : 'How to enable' }}
                </button>
              </div>
              <div v-if="showDeniedHelp" class="mt-2 pt-2 border-t border-red-200 dark:border-red-800/30">
                <p class="text-xs text-text-secondary font-medium mb-1">To re-enable:</p>
                <ol class="text-xs text-text-secondary list-decimal list-inside space-y-0.5">
                  <li>Click the lock icon in the address bar</li>
                  <li>Open <strong>Site settings</strong></li>
                  <li>Set <strong>Notifications</strong> to <strong>Allow</strong></li>
                  <li>Reload the page</li>
                </ol>
              </div>
            </div>
            <!-- Prompt: iOS unsupported -->
            <div v-if="showIOSPrompt" class="mx-4 mt-3 mb-1 px-4 py-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-xl">
              <div class="flex items-center gap-2">
                <svg class="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p class="text-xs text-text-secondary flex-1">Not available on iOS Safari</p>
                <button
                    @click="showIOSHelp = !showIOSHelp"
                    class="text-xs text-amber-600 dark:text-amber-400 underline hover:no-underline whitespace-nowrap"
                >
                  {{ showIOSHelp ? 'Hide' : 'How to enable' }}
                </button>
              </div>
              <div v-if="showIOSHelp" class="mt-2 pt-2 border-t border-amber-200 dark:border-amber-800/30">
                <p class="text-xs text-text-secondary font-medium mb-1">Add to home screen:</p>
                <ol class="text-xs text-text-secondary list-decimal list-inside space-y-0.5">
                  <li>Tap <strong>Share</strong> in Safari</li>
                  <li>Tap <strong>Add to Home Screen</strong></li>
                  <li>Open from your home screen</li>
                  <li>Allow notifications when prompted</li>
                </ol>
              </div>
            </div>
            <div v-if="loading" class="flex items-center justify-center py-12">
              <svg class="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>

            <div v-else-if="notifications.length === 0" class="flex flex-col items-center justify-center py-12 px-4">
              <svg class="w-16 h-16 text-text-secondary mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <p class="text-text-secondary text-sm">No notifications</p>
              <p class="text-text-secondary text-xs mt-1">You'll be notified when an admin responds to your orders</p>
            </div>

            <div v-else class="divide-y divide-background-lighter">
              <div
                  v-for="notif in notifications"
                  :key="notif.id"
                  @click="handleClick(notif)"
                  :class="[
                    'flex items-start gap-3 px-5 py-4 cursor-pointer transition-colors',
                    notif.is_read ? 'hover:bg-background-lighter/50' : 'bg-primary/5 hover:bg-primary/10'
                  ]"
              >
                <span class="text-xl flex-shrink-0 mt-0.5">{{ notif.icon || '🔔' }}</span>
                <div class="flex-1 min-w-0">
                  <p :class="['text-sm font-medium', notif.is_read ? 'text-text' : 'text-text font-semibold']">
                    {{ notif.title }}
                  </p>
                  <p v-if="notif.message" class="text-xs text-text-secondary mt-0.5 line-clamp-2">{{ notif.message }}</p>
                  <p class="text-xs text-text-secondary mt-1">{{ timeAgo(notif.created_at) }}</p>
                </div>
                <div v-if="!notif.is_read" class="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2"></div>
              </div>
            </div>

            <div v-if="hasMore()" class="px-5 py-3 text-center border-t border-background-lighter">
              <button
                  @click="loadMore"
                  class="text-sm text-primary hover:text-primary-dark font-medium transition-colors"
              >
                See more
              </button>
            </div>
          </div>

          <div class="px-5 py-3 border-t border-background-lighter text-center">
            <router-link
                to="/panel/notifications"
                class="text-sm text-primary hover:text-primary-dark font-medium transition-colors"
                @click="close"
            >
              View all notifications
            </router-link>
          </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { getNotifications, getUnreadNotificationsCount, markAllNotificationsAsRead, markNotificationAsRead } from '@/services/api.js'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useRealtimeNotifications } from '@/composables/useRealtimeNotifications'
import { useBrowserNotifications } from '@/composables/useBrowserNotifications'

const router = useRouter()
const authStore = useAuthStore()
const open = ref(false)
const loading = ref(false)
const notifications = ref([])
const unreadCount = ref(0)
const currentPage = ref(1)
const lastPage = ref(1)
const container = ref(null)
let pollInterval = null

const { isSupported, shouldAutoRequest, requestPermission, permission, isUnsupportedPlatform } = useBrowserNotifications()
const showNotifPrompt = ref(false)
const showDeniedPrompt = ref(false)
const showIOSPrompt = ref(false)
const showDeniedHelp = ref(false)
const showIOSHelp = ref(false)

const enableDesktopNotifs = async () => {
  try {
    const result = await requestPermission()
    if (result === 'denied') {
      showDeniedHelp.value = true
    } else {
      showNotifPrompt.value = false
    }
  } catch {
    // ignore
  }
}

const { latestNotification, subscribe } = useRealtimeNotifications()

watch(latestNotification, (notif) => {
  if (!notif) return
  unreadCount.value++
  notifications.value.unshift(notif)
  if (notifications.value.length > 50) {
    notifications.value = notifications.value.slice(0, 50)
  }
})

const hasMore = () => currentPage.value < lastPage.value

const pollUnreadCount = async () => {
  if (!authStore.isLoggedIn) return
  try {
    const data = await getUnreadNotificationsCount()
    unreadCount.value = data?.unread_count || 0
  } catch {
    // silent
  }
}

const fetch = async (page = 1) => {
  loading.value = true
  try {
    const result = await getNotifications(page)
    if (page === 1) {
      notifications.value = result.data || []
    } else {
      notifications.value = [...notifications.value, ...(result.data || [])]
    }
    unreadCount.value = result.unread_count || 0
    currentPage.value = result.meta?.current_page || 1
    lastPage.value = result.meta?.last_page || 1
  } catch {
    // silent
  } finally {
    loading.value = false
  }
}

const loadMore = () => {
  if (hasMore()) fetch(currentPage.value + 1)
}

const toggle = () => {
  if (!authStore.isLoggedIn) return
  open.value = !open.value
  if (open.value) {
    if (notifications.value.length === 0) fetch()
    if (isSupported()) {
      showNotifPrompt.value = shouldAutoRequest()
      showDeniedPrompt.value = permission() === 'denied'
      showIOSPrompt.value = false
    } else if (isUnsupportedPlatform()) {
      showNotifPrompt.value = false
      showDeniedPrompt.value = false
      showIOSPrompt.value = true
    } else {
      showNotifPrompt.value = false
      showDeniedPrompt.value = false
      showIOSPrompt.value = false
    }
  }
}

const close = () => {
  open.value = false
}

const handleClick = async (notif) => {
  if (!notif.is_read) {
    await markNotificationAsRead(notif.id)
    notif.is_read = true
    unreadCount.value = Math.max(0, unreadCount.value - 1)
  }
  close()
  let url = notif.action_url
  if (notif.action_url === '/panel/orders' && notif.data && notif.data.commande_id) {
    url = `/panel/orders?id=esk-${notif.data.commande_id}`
  }
  if (url) {
    router.push(url)
  }
}

const markAllRead = async () => {
  try {
    await markAllNotificationsAsRead()
    notifications.value.forEach(n => (n.is_read = true))
    unreadCount.value = 0
  } catch {
    // silent
  }
}

const timeAgo = (dateStr) => {
  if (!dateStr) return ''
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now - date
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHour < 24) return `${diffHour}h ago`
  if (diffDay < 7) return `${diffDay}d ago`
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const handleClickOutside = (e) => {
  if (container.value && !container.value.contains(e.target)) {
    open.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  pollUnreadCount()
  pollInterval = setInterval(pollUnreadCount, 60000)
  if (authStore.isLoggedIn) {
    subscribe()
  }
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
  if (pollInterval) clearInterval(pollInterval)
})
</script>

<style scoped>
.notif-enter-active {
  transition: opacity 0.2s ease;
}
.notif-leave-active {
  transition: opacity 0.15s ease;
}
.notif-enter-from,
.notif-leave-to {
  opacity: 0;
}

.animate-slideDown {
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
